// src/components/BlogModal.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import "./Blogs.css";

let DOMPURIFY_HOOKED = false; // ensure we don’t register the hook multiple times

// Very small helper to estimate reading time if not provided
function stripHtml(html = "") {
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").trim();
}

export default function BlogModal({ slug, onClose }) {
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const sheetRef = useRef(null);
    const prevFocusRef = useRef(null);
    const prevOverflowRef = useRef("");

    // Optional: allow only a few inline styles via DOMPurify hook (one-time)
    useEffect(() => {
        if (!DOMPURIFY_HOOKED) {
            const ALLOWED_CSS = /^(color|background-color|text-decoration|text-align)\s*:/i;
            DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
                if (data.attrName === "style") {
                    const cleaned = data.attrValue
                        .split(";")
                        .map((s) => s.trim())
                        .filter((s) => ALLOWED_CSS.test(s))
                        .join("; ");
                    if (cleaned) data.attrValue = cleaned;
                    else data.keepAttr = false;
                }
                if (node.nodeName === "A" && data.attrName === "target") {
                    // enforce safe linking
                    node.setAttribute("rel", "noopener noreferrer");
                }
            });
            DOMPURIFY_HOOKED = true;
        }
    }, []);

    // Fetch blog post by slug
    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                setLoading(true);
                setErr("");
                const { data } = await axios.get(`/api/blogs/${slug}`);
                if (mounted) setPost(data);
            } catch (e) {
                console.error(e);
                if (mounted) setErr("Failed to load blog.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => { mounted = false; };
    }, [slug]);

    // Lock scroll, save/restore focus, Escape & focus trap
    useEffect(() => {
        prevOverflowRef.current = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        prevFocusRef.current = document.activeElement;
        // move focus into modal after paint
        const t = setTimeout(() => {
            sheetRef.current?.focus();
        }, 0);

        const onKeyDown = (e) => {
            // Close on Escape
            if (e.key === "Escape") {
                e.preventDefault();
                onClose?.();
            }
            // Basic focus trap
            if (e.key === "Tab" && sheetRef.current) {
                const focusables = sheetRef.current.querySelectorAll(
                    'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
                );
                const list = Array.from(focusables).filter((el) => !el.hasAttribute("disabled"));
                if (list.length === 0) return;

                const first = list[0];
                const last = list[list.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => {
            clearTimeout(t);
            window.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = prevOverflowRef.current || "";
            // restore focus to the previously focused element
            prevFocusRef.current && prevFocusRef.current.focus?.();
        };
    }, [onClose]);

    const sanitizedHtml = useMemo(() => {
        const raw = post?.body || "";
        return DOMPurify.sanitize(raw, {
            ALLOWED_TAGS: [
                "p", "br", "ul", "ol", "li", "strong", "em", "u", "s", "a",
                "h1", "h2", "h3", "h4", "blockquote", "img", "span", "code", "pre"
            ],
            ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "style"],
            ALLOW_DATA_ATTR: false,
        });
    }, [post?.body]);

    const readingMinutes = useMemo(() => {
        if (!post) return 0;
        if (typeof post.readingMinutes === "number") return post.readingMinutes;
        const words = stripHtml(post.body || "").split(/\s+/).filter(Boolean).length;
        return Math.max(2, Math.round(words / 220));
    }, [post]);

    return (
        <div
            className="blog-modal-overlay"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="blog-modal-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="blog-modal-title"
                aria-describedby="blog-modal-body"
                onClick={(e) => e.stopPropagation()}
                ref={sheetRef}
                tabIndex={-1}
            >
                <button className="blog-modal-close" onClick={onClose} aria-label="Close">×</button>

                {loading && <div className="blog-modal-status">Loading…</div>}
                {err && !loading && <div className="blog-modal-status error">{err}</div>}

                {!loading && !err && post && (
                    <article className="blog-modal-article">
                        {post.heroImage && (
                            <img src={post.heroImage} alt="" className="blog-modal-hero" />
                        )}
                        <h2 id="blog-modal-title" className="blog-modal-title">
                            {post.title}
                        </h2>
                        <div className="blog-modal-meta">
                            {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : ""} · {readingMinutes} min
                        </div>

                        <div
                            id="blog-modal-body"
                            className="blog-modal-body"
                            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                        />
                    </article>
                )}
            </div>
        </div>
    );
}
